import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, MapPin, ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { COMPANY, IMAGES, waLink } from "../lib/constants";

const SECTIONS = [
  {
    title: "Who we are",
    text: `Krishnaa Constructions — KAC — is a UK building company led by ${COMPANY.contact}. We carry out all types of building work: loft conversions, house extensions, kitchen and bathroom renovations, electrical, plumbing, tiling and general building. Every project, whatever its size, gets the same care and attention.`,
  },
  {
    title: "Our approach to construction",
    text: "Good building work starts long before the first brick. We begin every project by listening — understanding how you use your home and what you want from the space — then plan the work properly so it runs to time and to the agreed quote.",
  },
  {
    title: "Quality & attention to detail",
    text: "We believe the difference is in the finish: straight lines, clean joints, tidy edges and sites left swept at the end of each day. Work is checked as it progresses, not just at the end, so the standard stays high from start to handover.",
  },
  {
    title: "Customer communication",
    text: `You deal directly with ${COMPANY.contact} throughout your project. You'll always know what's happening, what happens next, and who to call — no call centres, no being passed around.`,
  },
  {
    title: "Our team",
    text: "KAC brings together the trades your project needs — builders, electricians, plumbers and tilers — coordinated as one team, so you're never left managing multiple contractors yourself.",
  },
];

export default function About() {
  useEffect(() => {
    document.title = "About Us | KAC — Krishnaa Constructions";
  }, []);
  return (
    <>
      <section data-testid="about-hero" className="bg-brand-green py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-white/70">About KAC</p>
            <h1 className="mt-4 max-w-3xl font-heading text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl">
              Quality Workmanship. <span className="underline decoration-brand-red decoration-4 underline-offset-8">A Commitment You Can Trust.</span>
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-5 lg:px-8">
          <div className="space-y-12 lg:col-span-3">
            {SECTIONS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.05}>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-brand-green">{s.title}</h2>
                  <p className="mt-3 leading-relaxed text-brand-muted">{s.text}</p>
                </div>
              </Reveal>
            ))}
            <Reveal>
              <div className="rounded-2xl border border-brand-light bg-brand-light/50 p-7">
                <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-brand-green">
                  <MapPin className="h-6 w-6 text-brand-red" /> Where we work
                </h2>
                <p className="mt-3 leading-relaxed text-brand-muted">
                  We take on projects across {COMPANY.areas}. If you're further afield, get in touch — we'll always tell you
                  honestly whether we can take your project on.
                </p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-2">
            <Reveal delay={0.1} className="lg:sticky lg:top-28">
              <img src={IMAGES.loft} alt="Bright converted loft space, illustrative" className="aspect-[4/5] w-full rounded-2xl object-cover shadow-xl" />
              <div className="mt-6 rounded-2xl bg-brand-green p-7 text-white">
                <h3 className="font-heading text-xl font-bold">Ready to talk about your project?</h3>
                <p className="mt-2 text-sm text-white/80">Free, no-obligation quotes — call, WhatsApp or send the form.</p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <a href={`tel:${COMPANY.phone}`} data-testid="about-call-btn" className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-brand-green transition-transform active:scale-95">
                    <Phone className="h-4 w-4" /> Call
                  </a>
                  <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="about-whatsapp-btn" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-bold text-white transition-transform active:scale-95">
                    <MessageCircle className="h-4 w-4" /> WhatsApp
                  </a>
                  <Link to="/contact" data-testid="about-quote-btn" className="inline-flex items-center gap-2 rounded-full bg-brand-red px-5 py-2.5 text-sm font-bold text-white transition-transform active:scale-95">
                    Free Quote <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
